import {AlertTriangle} from 'lucide-react'
import {useI18n} from '@/i18n'
import {routes} from '@/lib/routes'
import {CardSection} from '@/components/shared/CardSection'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'
import {Badge} from '@/components/ui/badge'
import {TextLink} from '@/components/shared/TextLink'

/**
 * One {@code AuthorityViolation} record carried in the inbound sync run
 * completion event. Mirrors the backend record at
 * {@code com.irian.iam.attribute.api.AuthorityViolation}.
 */
export interface AuthorityViolation {
    entityType: string
    entityId: string
    entityName?: string | null
    attributeName: string
    ownerSystemId?: string | null
    ownerSystemName?: string | null
    syncSystemId?: string | null
    syncSystemName?: string | null
}

export interface AuthorityViolationsStats {
    attributesAppliedCount?: number
    attributesSkippedAuthorityCount?: number
    authorityViolationsTotal?: number
    authorityViolations?: AuthorityViolation[]
    authorityViolationsTruncated?: boolean
}

export interface AuthorityViolationsSectionProps {
    stats: Record<string, unknown> | null | undefined
}

const VIOLATION_DISPLAY_CAP = 100

/**
 * Authority Violations section on the inbound sync run summary page (Spec
 * §6.4). Renders one of three states based on the run's
 * {@code attributesSkippedAuthorityCount}:
 *
 *   - 0 — section hidden entirely (returns null)
 *   - 1-100 — inline list with one row per violation
 *   - > 100 — first 100 rows + truncation banner
 */
export function AuthorityViolationsSection({stats}: AuthorityViolationsSectionProps) {
    const i18n = useI18n()
    if (!stats) return null
    const typed = stats as AuthorityViolationsStats
    const total = typed.attributesSkippedAuthorityCount
        ?? typed.authorityViolationsTotal
        ?? typed.authorityViolations?.length
        ?? 0
    if (total <= 0) return null

    const violations = typed.authorityViolations ?? []
    const truncated = typed.authorityViolationsTruncated === true
        || total > violations.length
        || violations.length > VIOLATION_DISPLAY_CAP
    const visible = violations.slice(0, VIOLATION_DISPLAY_CAP)

    return (
        <CardSection
            icon={AlertTriangle}
            title={i18n.authority.violations.title as string}
            description={i18n.authority.violations.count({count: total})}
            data-testid="section-authority-violations"
            data-help-prefix="authorityViolationsSection"
        >
            {visible.length > 0 && (
                <Stack gap="xs" divideY data-testid="authority-violations-list">
                    {visible.map((violation, idx) => (
                        <ViolationRow
                            key={`${violation.entityType}-${violation.entityId}-${violation.attributeName}-${idx}`}
                            violation={violation}
                        />
                    ))}
                </Stack>
            )}
            {truncated && (
                <Stack gap="xs" data-testid="authority-violations-truncated">
                    <MutedText size="sm">{i18n.authority.violations.truncated}</MutedText>
                </Stack>
            )}
        </CardSection>
    )
}

interface ViolationRowProps {
    violation: AuthorityViolation
}

function ViolationRow({violation}: ViolationRowProps) {
    const i18n = useI18n()
    const entityName = violation.entityName ?? violation.entityId
    const ownerSystemName = violation.ownerSystemName ?? ''
    const syncSystemName = violation.syncSystemName ?? ''
    const linkTo = entityDetailRoute(violation.entityType, violation.entityId)

    return (
        <InlineRow
            gap="md"
            align="center"
            className="py-2"
            data-testid={`authority-violation-row-${violation.entityType}-${violation.entityId}`}
        >
            <Badge variant="outline">{violation.entityType}</Badge>
            <div className="flex-1 min-w-0">
                {linkTo ? (
                    <TextLink to={linkTo} testId={`authority-violation-link-${violation.entityId}`}>
                        {entityName}
                    </TextLink>
                ) : (
                    <Strong>{entityName}</Strong>
                )}
                <MutedText size="xs" className="mt-0.5">
                    {i18n.authority.violations.rowLabel({
                        entityType: violation.entityType,
                        entityName,
                        attributeName: violation.attributeName,
                        ownerSystem: ownerSystemName,
                        syncSystem: syncSystemName,
                    })}
                </MutedText>
            </div>
        </InlineRow>
    )
}

function entityDetailRoute(entityType: string, entityId: string): string | undefined {
    const lowered = entityType.toLowerCase()
    if (lowered === 'identity' || lowered === 'identities') {
        return routes.identityManagement.identities.detail(entityId)
    }
    if (lowered === 'party' || lowered === 'parties') {
        return routes.identityManagement.parties.detail(entityId)
    }
    return undefined
}
